<template>
  <div v-if="isVisible">
    <!-- Bootstrap Modal -->
    <div class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-shield-check me-2"></i>
              Verification Required
            </h5>
            <button v-if="allowClose" type="button" class="btn-close" @click="close" aria-label="Close"></button>
          </div>
          
          <div class="modal-body">
            <!-- Error Alert -->
            <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
              <button type="button" class="btn-close" @click="errorMessage = ''" aria-label="Close"></button>
            </div>
            
            <!-- Success Alert -->
            <div v-if="successMessage" class="alert alert-success" role="alert">
              <i class="bi bi-check-circle-fill me-2"></i>
              {{ successMessage }}
            </div>
            
            <!-- Instructions -->
            <div class="mb-4">
              <p class="text-muted">
                {{ instructionText }}
              </p>
            </div>
            
            <!-- Code Input Form -->
            <form @submit.prevent="submitCode">
              <div class="mb-3">
                <label for="verificationCode" class="form-label">Verification Code</label>
                <div class="input-group input-group-lg">
                  <span class="input-group-text">
                    <i class="bi bi-key"></i>
                  </span>
                  <input
                    ref="codeInput"
                    v-model="verificationCode"
                    type="text"
                    class="form-control text-center"
                    id="verificationCode"
                    placeholder="Enter code"
                    maxlength="8"
                    :disabled="isVerifying"
                    autocomplete="one-time-code"
                  >
                </div>
                <div class="form-text">
                  Enter the {{ codeLength }}-digit code sent to {{ maskedDestination }}
                </div>
              </div>
              
              <!-- Resend Code -->
              <div v-if="canResend" class="mb-3">
                <button 
                  type="button" 
                  class="btn btn-link p-0" 
                  @click="resendCode"
                  :disabled="isResending"
                >
                  <span v-if="isResending">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                    Sending...
                  </span>
                  <span v-else>
                    Didn't receive a code? Send again
                  </span>
                </button>

              </div>
              <div v-else-if="resendCooldown > 0" class="small text-muted">
                  You can resend in {{ resendCooldown }} seconds
              </div>
            </form>
          </div>
          
          <div class="modal-footer">
            <button 
              v-if="allowClose" 
              type="button" 
              class="btn btn-secondary" 
              @click="close" 
              :disabled="isVerifying"
            >
              Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="submitCode" 
              :disabled="!canSubmit || isVerifying"
            >
              <span v-if="isVerifying" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ isVerifying ? 'Verifying...' : 'Verify' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal Backdrop -->
    <div class="modal-backdrop fade show" @click="handleBackdropClick"></div>
  </div>
</template>

<script>
export default {
  name: 'TwoFactorAuth',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    allowClose: {
      type: Boolean,
      default: true
    },
    codeLength: {
      type: Number,
      default: 6
    },
    resendDelay: {
      type: Number,
      default: 30 // seconds
    }
  },
  emits: ['update:modelValue', 'verified', 'cancelled', 'error'],
  data() {
    return {
      verificationCode: '',
      isVerifying: false,
      isResending: false,
      errorMessage: '',
      successMessage: '',
      isVisible: false,
      internalContext: {},
      resendCooldown: 0,
      cooldownTimer: null,
      maskedDestination: '',
      deliveryMethod: 'email', // or 'sms'
      initialCodeSent: false,
      initialSendCooldown: 0,
      initialSendCooldownTimer: null
    };
  },
  computed: {
    vcCommunicator() {
      return window.vcCommunicator || null;
    },
    canSubmit() {
      return this.verificationCode.trim().length === this.codeLength;
    },
    canResend() {
      return this.resendCooldown === 0;
    },
    instructionText() {
      if (this.deliveryMethod === 'sms') {
        return 'We\'ve sent a verification code to your phone number.';
      }
      return 'We\'ve sent a verification code to your email address.';
    }
  },
  mounted() {
    if (window.twoFactorService) {
      window.twoFactorService.registerComponent(this);
    }
    console.log('TwoFactorAuth component mounted');
  },
  watch: {
    modelValue(newVal) {
      this.isVisible = newVal;
    },
    isVisible(newVal) {
      if (newVal) {
        this.reset();
        // Only send initial code if not in cooldown
        if (!this.initialCodeSent && this.initialSendCooldown === 0) {
          this.sendInitialCode();
          this.startInitialSendCooldown();
        }
        this.$nextTick(() => {
          this.$refs.codeInput?.focus();
        });
      }
    }
  },
  beforeUnmount() {
    console.log('TwoFactorAuth component unmounting');
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
    }
    if (this.initialSendCooldownTimer) {
      clearInterval(this.initialSendCooldownTimer);
    }
  },
  methods: {
    showWithContext(context) {
      console.log('showWithContext called with:', context);
      this.internalContext = context || {};
      this.isVisible = true;
      this.$emit('update:modelValue', true);
    },
    
    async sendInitialCode() {
      if (!this.vcCommunicator) return;
      
      try {
        const response = await this.vcCommunicator.send2FACode(this.internalContext);
        
        if (response.success || !response.errorCode) {
          this.maskedDestination = response.maskedDestination || 'your registered email';
          this.deliveryMethod = response.deliveryMethod || 'email';
          this.startResendCooldown();
          this.startInitialSendCooldown();
        } else {
          this.errorMessage = response.message || 'Failed to send verification code.';
        }
      } catch (error) {
        console.error('Failed to send initial code:', error);
        this.errorMessage = 'Failed to send verification code. Please try again.';
      }
    },
    
    async resendCode() {
      if (!this.canResend || this.isResending) return;
      
      this.isResending = true;
      this.errorMessage = '';
      this.successMessage = '';
      
      try {
        const response = await this.vcCommunicator.resend2FACode(this.internalContext);
        
        if (response.success || !response.errorCode) {
          this.successMessage = 'Verification code sent successfully!';
          this.startResendCooldown();
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        } else {
          this.errorMessage = response.message || 'Failed to resend code.';
        }
      } catch (error) {
        console.error('Failed to resend code:', error);
        this.errorMessage = 'Failed to resend code. Please try again.';
      } finally {
        this.isResending = false;
      }
    },
    
    startResendCooldown() {
      this.resendCooldown = this.resendDelay;
      
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
      }
      
      this.cooldownTimer = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) {
          clearInterval(this.cooldownTimer);
        }
      }, 1000);
    },
    
    startInitialSendCooldown() {
      this.initialCodeSent = true;
      this.initialSendCooldown = this.resendDelay; // Use same delay as resend
      if (this.initialSendCooldownTimer) {
        clearInterval(this.initialSendCooldownTimer);
      }
      this.initialSendCooldownTimer = setInterval(() => {
        this.initialSendCooldown--;
        if (this.initialSendCooldown <= 0) {
          clearInterval(this.initialSendCooldownTimer);
          this.initialCodeSent = false;
        }
      }, 1000);
    },
    
    async submitCode() {
      if (!this.canSubmit || this.isVerifying) return;
      
      this.isVerifying = true;
      this.errorMessage = '';
      
      try {
        const response = await this.vcCommunicator.verify2FACode({
          code: this.verificationCode,
          ...this.internalContext
        });
        
        if (response.success || !response.errorCode) {
          this.handleVerified({
            context: this.internalContext,
            response: response
          });
          this.isVisible = false;
          this.$emit('update:modelValue', false);
        } else {
          this.errorMessage = response.message || 'Invalid code. Please try again.';
          this.verificationCode = '';
          this.$nextTick(() => {
            this.$refs.codeInput?.focus();
          });
        }
      } catch (error) {
        console.error('2FA verification error:', error);
        this.errorMessage = `An error occurred. ${error || ''}`;
        this.handleError(error);
      } finally {
        this.isVerifying = false;
      }
    },
    
    handleBackdropClick() {
      if (this.allowClose && !this.isVerifying) {
        this.close();
      }
    },
    
    close() {
      if (this.isVerifying) return;
      this.isVisible = false;
      this.$emit('update:modelValue', false);
      this.handleCancelled();
    },
    
    reset() {
      this.verificationCode = '';
      this.errorMessage = '';
      this.successMessage = '';
      this.isVerifying = false;
      this.isResending = false;
      this.resendCooldown = 0;
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
      }
      // Do NOT reset initialCodeSent or initialSendCooldown here
      // so cooldown persists even if modal is hidden
    },
    
    handleVerified(result) {
      this.$emit('verified', result);
      if (this._twoFactorHandlers && this._twoFactorHandlers.verified) {
        this._twoFactorHandlers.verified(result);
        delete this._twoFactorHandlers;
      }
    },
    
    handleCancelled() {
      this.$emit('cancelled');
      if (this._twoFactorHandlers && this._twoFactorHandlers.cancelled) {
        this._twoFactorHandlers.cancelled();
        delete this._twoFactorHandlers;
      }
    },
    
    handleError(error) {
      this.$emit('error', error);
      if (this._twoFactorHandlers && this._twoFactorHandlers.error) {
        this._twoFactorHandlers.error(error);
        delete this._twoFactorHandlers;
      }
    }
  }
};
</script>

<style scoped>
/* Minimal custom styles - rely on Bootstrap */
#verificationCode {
  font-size: 1.25rem;
  letter-spacing: 0.25rem;
  font-family: monospace;
}
</style>